import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { setCurrentPage, setFilters, setPageSize } from 'dashboard/redux/actions/events';
import { showMessage } from 'dashboard/redux/actions/messages';
import { getEventsInitialState } from 'dashboard/redux/reducers/events';
import { RootState } from 'dashboard/redux/store';
import { loadEventList } from 'dashboard/redux/thunk-actions/events';
import DashboardEventsView from 'dashboard/views/DashboardEventsView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

type DashboardEventsRouteProps = PropsFromRedux;

const DashboardEventsRoute = (props: DashboardEventsRouteProps) => {
  const { currentEnvironment, currentPage, filters, pageSize } = props;

  const { handlePageLinkClick, handlePageSizeClick, handleDateUpdate, handleOmniSearch } = useTable(
    {
      filters: filters,
      pageSize: pageSize,
      currentPage: currentPage,
      setFilters: props.setFilters,
      setCurrentPage: props.setCurrentPage,
      setPageSize: props.setPageSize,
    },
  );

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        if (startDate || endDate) handleDateUpdate({ startDate, endDate });
      }
    },
    [handleDateUpdate],
  );

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadEventList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.filters,
    ],
  );

  const { loading } = useApiLoader({
    loader: () =>
      props.loadEventList({
        currentEnvironment,
        currentPage: currentPage || 1,
        pageSize: pageSize || 50,
        filters: filters || {},
        abortSignal: abortController.signal,
      }),
    deps: [props.loadEventList, currentEnvironment, currentPage, pageSize, filters],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load events.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const handleNamesUpdate = (names: string[]) => {
    props.setFilters({ names });
    handlePageLinkClick(1);
  };

  // Initially reload the Events filters state createdAtStart and createdAtEnd
  useEffect(() => {
    const initialFilters = getEventsInitialState().filters;
    props.setFilters({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      createdAtStart: initialFilters.createdAtStart,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      createdAtEnd: initialFilters.createdAtEnd,
    });
  }, [props.setFilters]);

  return (
    <DashboardEventsView
      events={props.events}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      isLoading={loading}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handleNamesUpdate={handleNamesUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    events: state.events.events[state.currentEnvironment.currentEnvironment.id] || [],
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    pageSize: state.events.pageSize,
    currentPage: state.events.currentPage,
    paginationParams: state.events.paginationParams,
    filters: state.events.filters,
  };
};

const mapDispatchToProps = {
  loadEventList,
  setFilters,
  setPageSize,
  setCurrentPage,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardEventsRoute);
